import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useModuleAvailability } from "@playbookai/api-clients";
import { KNOWLEDGE_ARTICLES, KNOWLEDGE_DOMAINS } from "./content/index.js";
import { moduleForKnowledgeDomain } from "./content/domain-modules.js";
import type {
  KnowledgeArticle,
  KnowledgeAudience,
  KnowledgeDomain,
  KnowledgeFigure,
  KnowledgeLearningScope,
} from "./content/types.js";
import { allowedKnowledgeArticles } from "./content/audience.js";
import { KNOWLEDGE_FIGURES } from "./content/figures.js";
import {
  learningStorageKey,
  readLearningProgress,
  saveLearningProgress,
} from "./content/progress.js";

interface KnowledgeStudioContextValue {
  readonly articles: readonly KnowledgeArticle[];
  readonly domains: readonly KnowledgeDomain[];
  readonly figures: Readonly<Record<string, KnowledgeFigure>>;
  readonly audience: KnowledgeAudience;
  readonly canViewAdmin: boolean;
  readonly completed: readonly string[];
  readonly adminState: "loading" | "ready" | "error";
  /**
   * The "turned off" sentence when a group's module is off (ADR-0100), for a
   * direct link to one of its hidden pages; `null` otherwise.
   */
  readonly unavailableDomainMessage: (
    domainId: string | undefined,
  ) => string | null;
  readonly setAudience: (audience: KnowledgeAudience) => void;
  readonly toggleReviewed: (id: string) => void;
  readonly retryAdmin: () => void;
}

const KnowledgeStudioContext =
  createContext<KnowledgeStudioContextValue | null>(null);

export interface KnowledgeStudioProviderProps {
  readonly canViewAdmin?: boolean;
  readonly learningScope?: KnowledgeLearningScope;
  readonly children: ReactNode;
}

export function KnowledgeStudioProvider({
  canViewAdmin = false,
  learningScope,
  children,
}: KnowledgeStudioProviderProps): JSX.Element {
  const [params, setParams] = useSearchParams();
  const requestedAudience = params.get("audience");
  const audience: KnowledgeAudience =
    requestedAudience === "facilitators"
      ? "facilitators"
      : requestedAudience === "admins" && canViewAdmin
        ? "admins"
        : "participants";
  const [adminArticles, setAdminArticles] = useState<
    readonly KnowledgeArticle[]
  >([]);
  const [adminState, setAdminState] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [adminAttempt, setAdminAttempt] = useState(0);
  const key = learningStorageKey(learningScope);
  const [progress, setProgress] = useState(() => ({
    key,
    ids: readLearningProgress(key),
  }));
  // Scope changes are reflected in this render. No effect writes one person's
  // progress into another person's storage key.
  const completed = useMemo(
    () => (progress.key === key ? progress.ids : readLearningProgress(key)),
    [progress, key],
  );

  useEffect(() => {
    if (!canViewAdmin) return;
    let current = true;
    setAdminState("loading");
    void import("./content/admin.js").then(
      ({ ADMIN_ARTICLES }) => {
        if (!current) return;
        setAdminArticles(ADMIN_ARTICLES);
        setAdminState("ready");
      },
      () => {
        if (current) setAdminState("error");
      },
    );
    return () => {
      current = false;
    };
  }, [canViewAdmin, adminAttempt]);

  const availability = useModuleAvailability();
  const unavailableDomainMessage = useCallback(
    (domainId: string | undefined) => {
      const key = moduleForKnowledgeDomain(domainId);
      return key === null ? null : availability.unavailableMessage(key);
    },
    [availability],
  );
  // The one filtering point: every listing, search, progress count and the
  // screen help read this catalogue, so a guide about an off module is gone
  // from all of them at once.
  const articles = useMemo(
    () =>
      allowedKnowledgeArticles(
        [...KNOWLEDGE_ARTICLES, ...(canViewAdmin ? adminArticles : [])],
        canViewAdmin,
      ).filter(
        (article) => unavailableDomainMessage(article.domainId) === null,
      ),
    [canViewAdmin, adminArticles, unavailableDomainMessage],
  );
  const domains = useMemo(
    () =>
      KNOWLEDGE_DOMAINS.filter((domain) =>
        domain.id === "admin"
          ? canViewAdmin
          : articles.some((article) => article.domainId === domain.id),
      ),
    [articles, canViewAdmin],
  );
  const setAudience = useCallback(
    (next: KnowledgeAudience) => {
      if (next === "admins" && !canViewAdmin) return;
      setParams((previous) => {
        const result = new URLSearchParams(previous);
        result.set("audience", next);
        return result;
      });
    },
    [canViewAdmin, setParams],
  );
  const toggleReviewed = useCallback(
    (id: string) => {
      if (!articles.some((article) => article.id === id)) return;
      setProgress((previous) => {
        const existing =
          previous.key === key ? previous.ids : readLearningProgress(key);
        const ids = existing.includes(id)
          ? existing.filter((item) => item !== id)
          : [...existing, id];
        saveLearningProgress(key, ids);
        return { key, ids };
      });
    },
    [articles, key],
  );
  const retryAdmin = useCallback(
    () => setAdminAttempt((value) => value + 1),
    [],
  );
  const value = useMemo(
    () => ({
      articles,
      domains,
      figures: KNOWLEDGE_FIGURES,
      audience,
      canViewAdmin,
      completed,
      adminState,
      unavailableDomainMessage,
      setAudience,
      toggleReviewed,
      retryAdmin,
    }),
    [
      articles,
      domains,
      audience,
      canViewAdmin,
      completed,
      adminState,
      unavailableDomainMessage,
      setAudience,
      toggleReviewed,
      retryAdmin,
    ],
  );
  return (
    <KnowledgeStudioContext.Provider value={value}>
      {children}
    </KnowledgeStudioContext.Provider>
  );
}

export function useKnowledgeStudio(): KnowledgeStudioContextValue {
  const value = useContext(KnowledgeStudioContext);
  if (!value) throw new Error("Knowledge Studio needs its provider.");
  return value;
}
